import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import PanelHeader from "./panelHeader";

const SliderDualNumeric = ({ textLen, label, color, callback, reset, initialValue }) => {
    // Split values into integer and decimal parts
    const splitValue = (value) => {
        const intPart = Math.floor(value);
        const decPart = Math.round((value - intPart) * 100); // 0-99 centiseconds
        return [intPart, decPart];
    };

    const combineValues = (secondsRange, centisecondsRange) => {
        return [
            secondsRange[0] + (centisecondsRange[0] / 100),
            secondsRange[1] + (centisecondsRange[1] / 100)
        ];
    };

    // Initialize values - split both min and max values
    const initialMin = initialValue ? splitValue(initialValue[0]) : splitValue(textLen[0]);
    const initialMax = initialValue ? splitValue(initialValue[1]) : splitValue(textLen[1]);

    const [secondsRange, setSecondsRange] = useState([initialMin[0], initialMax[0]]);
    // Always start centiseconds at [0, 0] for clean initialization
    const [centisecondsRange, setCentisecondsRange] = useState([0, 0]);
    const [isInitialized, setIsInitialized] = useState(false);

    // Calculate the overall min/max for seconds slider
    const overallMin = Math.floor(textLen[0]);
    const overallMax = Math.floor(textLen[1]);

    // Check if seconds range allows centiseconds precision
    const isCentisecondsEnabled = secondsRange[0] === secondsRange[1];

    // Calculate combined values for callback
    const combinedValues = combineValues(secondsRange, centisecondsRange);

    const handleSecondsChange = (event, newValues) => {
        setSecondsRange(newValues);

        // If seconds min and max are different, reset centiseconds to [0, 0]
        if (newValues[0] !== newValues[1]) {
            setCentisecondsRange([0, 0]);
        }
    };

    const handleCentisecondsChange = (event, newValues) => {
        setCentisecondsRange(newValues);
    };

    const handleChangeCommitted = () => {
        callback(combinedValues);
    };

    // Initialize component with textLen values
    useEffect(() => {
        if (!isInitialized) {
            const resetMin = splitValue(textLen[0]);
            const resetMax = splitValue(textLen[1]);
            setSecondsRange([resetMin[0], resetMax[0]]);
            // Always start centiseconds at [0, 0] for clean initialization
            setCentisecondsRange([0, 0]);
            setIsInitialized(true);
        }
    }, [textLen, isInitialized]);

    // Update when initialValue changes (including reset)
    useEffect(() => {
        if (initialValue && isInitialized) {
            const newMin = splitValue(initialValue[0]);
            const newMax = splitValue(initialValue[1]);
            setSecondsRange([newMin[0], newMax[0]]);
            setCentisecondsRange([newMin[1], newMax[1]]);
        }
    }, [initialValue, isInitialized]);


    return (
        <Box sx={{ width: '100%' }}>
            {label && label !== "" && (
                <PanelHeader
                    title={`${label} from: ${combinedValues[0].toFixed(2)}s to: ${combinedValues[1].toFixed(2)}s`}
                    underline={false}
                />
            )}

            {/* Seconds Range Slider */}
            <Box sx={{ mt: 1, mb: 2, p: 1, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                <Slider
                    value={secondsRange}
                    size="small"
                    valueLabelDisplay="on"
                    marks={true}
                    step={1}
                    min={overallMin}
                    max={overallMax}
                    onChange={handleSecondsChange}
                    color={color}
                    onChangeCommitted={handleChangeCommitted}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }} color="text.primary">
                        {overallMin} s
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }} color="text.primary">
                        {overallMax} s
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
                        Seconds Range: {secondsRange[0]} s - {secondsRange[1]} s
                    </Typography>
                </Box>
            </Box>

            {/* Centiseconds Range Slider */}
            <Box sx={{
                mt: 0,
                mb: 1,
                p: 1,
                bgcolor: isCentisecondsEnabled ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.01)',
                borderRadius: 1,
                opacity: isCentisecondsEnabled ? 1 : 0.5
            }}>
                {!isCentisecondsEnabled && (
                    <Typography variant="caption" sx={{
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.secondary',
                        mb: 1,
                        fontStyle: 'italic'
                    }}>
                        Set seconds min and max to the same value to enable centiseconds precision
                    </Typography>
                )}

                <Slider
                    value={centisecondsRange}
                    size="small"
                    valueLabelDisplay="on"
                    step={1}
                    min={0}
                    max={99}
                    onChange={handleCentisecondsChange}
                    color={color}
                    onChangeCommitted={handleChangeCommitted}
                    marks={true}
                    disabled={!isCentisecondsEnabled}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" sx={{
                        fontWeight: 'bold',
                        opacity: isCentisecondsEnabled ? 1 : 0.5
                    }} color="text.primary">
                        0 cs
                    </Typography>
                    <Typography variant="caption" sx={{
                        fontWeight: 'bold',
                        opacity: isCentisecondsEnabled ? 1 : 0.5
                    }} color="text.primary">
                        99 cs
                    </Typography>

                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                    <Typography variant="caption" sx={{
                        mb: 1,
                        display: 'block',
                        fontWeight: 'bold',
                        opacity: isCentisecondsEnabled ? 1 : 0.5
                    }}>
                        Centiseconds Range: {centisecondsRange[0]} cs - {centisecondsRange[1]} cs
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default SliderDualNumeric; 